import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Chip,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import { ArrowBack, Refresh, Search, AccountBalance, SwapHoriz, Info } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { transactionTypeColor } from '../utils/transactionTypes';

const TerminalTransactions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get the return state from navigation (search term, page, expanded panels)
  const returnState = location.state || {};

  // Navigate back to terminals with preserved search state and auto-expand location
  const handleBackToTerminals = useCallback(() => {
    const { returnSearch, returnPage, returnExpandedPanels, terminalMarket, terminalLocation } = returnState;

    // Auto-expand the market/store that contains this terminal
    const expandedPanels = { ...(returnExpandedPanels || {}) };

    if (terminalMarket) {
      expandedPanels[terminalMarket] = true;
    }
    if (terminalLocation) {
      // Also expand the store within the market
      const storeKey = `${terminalMarket || 'Other'}|${terminalLocation}`;
      expandedPanels[storeKey] = true;
      // For legacy locations without market
      expandedPanels[terminalLocation] = true;
    }

    navigate('/terminals', {
      state: {
        preserveSearch: true,
        searchTerm: returnSearch || '',
        page: returnPage || 0,
        expandedPanels,
        expandedStoreName: terminalLocation
      }
    });
  }, [navigate, returnState]);

  const [activeTab, setActiveTab] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    transactionType: '',
    responseCode: ''
  });

  const { getTerminalDailyTransactions, getTerminalTransactionSummary, getDailyTransactionsAnalytics } = useApi();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: pageSize,
        startDate: format(filters.startDate, 'yyyy-MM-dd'),
        endDate: format(filters.endDate, 'yyyy-MM-dd')
      };

      if (filters.transactionType) {
        params.transactionType = filters.transactionType;
      }
      if (filters.responseCode) {
        params.responseCode = filters.responseCode;
      }

      // Load transactions, summary, and analytics in parallel
      const [transactionsResponse, summaryResponse, analyticsResponse] = await Promise.all([
        getTerminalDailyTransactions(id, params),
        getTerminalTransactionSummary(id, {
          startDate: params.startDate,
          endDate: params.endDate,
          transactionType: filters.transactionType,
          responseCode: filters.responseCode
        }),
        getDailyTransactionsAnalytics({
          startDate: params.startDate,
          endDate: params.endDate,
          terminalId: id
        })
      ]);

      // Debug logs removed to improve performance
      
      setTransactions(transactionsResponse.transactions || []);
      setTotalPages(transactionsResponse.pagination?.totalPages || 1);
      setTotalRecords(transactionsResponse.pagination?.total || 0);
      setSummary(summaryResponse);
      setAnalytics(analyticsResponse);
    } catch (err) {
      setError(err.message || 'Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  }, [id, page, pageSize, filters.startDate, filters.endDate, filters.transactionType, filters.responseCode]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, page, pageSize, filters.startDate, filters.endDate, filters.transactionType, filters.responseCode]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  };

  const getPageInfo = () => {
    const start = Math.min((page - 1) * pageSize + 1, totalRecords);
    const end = Math.min(page * pageSize, totalRecords);
    return { start, end };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatDateTime = (date, time, timezone, localDateTime) => {
    if (!date || !time) return 'N/A';

    // transaction_time is stored as the terminal's LOCAL wall-clock time for ALL sources
    // (RPT and polled alike), so render it as-is. Do NOT re-parse localDateTime through
    // new Date(): its value is already local but carries a 'Z' suffix, so Date()+toTimeString()
    // re-applies the browser's offset and shifts the time ~4h (RPT rows showed 4h early — the
    // "weird time" bug, 2026-06-24). Just attach the zone label from the timezone column.
    const dateOnly = String(date).includes('T') ? String(date).split('T')[0] : date;

    const tz = String(timezone || '').toUpperCase();
    const tzAbbr = (tz.includes('NEW_YORK') || tz === 'ET' || tz === 'EST' || tz === 'EDT') ? 'ET' :
                   (tz.includes('CHICAGO') || tz === 'CT' || tz === 'CST' || tz === 'CDT') ? 'CT' :
                   (tz.includes('DENVER') || tz === 'MT' || tz === 'MST' || tz === 'MDT') ? 'MT' :
                   (tz.includes('LOS_ANGELES') || tz === 'PT' || tz === 'PST' || tz === 'PDT') ? 'PT' :
                   '';

    return `${dateOnly} ${time}${tzAbbr ? ' ' + tzAbbr : ''}`;
  };

  const getResponseCodeColor = (code) => {
    return code === 0 ? 'success' : 'error';
  };

  if (loading && !transactions.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}
        </Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBackToTerminals}>
            Back to Terminals
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToTerminals}
              sx={{ mr: 2 }}
            >
              Back to Terminals
            </Button>
            <Typography variant="h4">
              Terminal {id} - Daily Transactions
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              startIcon={<AccountBalance />}
              onClick={() => navigate(`/terminals/${id}`)}
              variant="contained"
              color="secondary"
            >
              View Settlement
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="end">
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    value={filters.transactionType}
                    label="Transaction Type"
                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="WTH">Withdrawal</MenuItem>
                    <MenuItem value="RWT">Reversal</MenuItem>
                    <MenuItem value="INQ">Inquiry</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Response Code</InputLabel>
                  <Select
                    value={filters.responseCode}
                    label="Response Code"
                    onChange={(e) => handleFilterChange('responseCode', e.target.value)}
                  >
                    <MenuItem value="">All Codes</MenuItem>
                    <MenuItem value="0">00 - Approved</MenuItem>
                    <MenuItem value="1">01 - Bank Decline</MenuItem>
                    <MenuItem value="5">05 - Do Not Honor</MenuItem>
                    <MenuItem value="6">06 - Comm Error</MenuItem>
                    <MenuItem value="12">12 - Invalid Transaction</MenuItem>
                    <MenuItem value="13">13 - Invalid Amount</MenuItem>
                    <MenuItem value="22">22 - Expired Card</MenuItem>
                    <MenuItem value="50">50 - Invalid PIN</MenuItem>
                    <MenuItem value="51">51 - Insufficient Funds</MenuItem>
                    <MenuItem value="61">61 - Exceeds Limit</MenuItem>
                    <MenuItem value="65">65 - Exceeds Freq Limit</MenuItem>
                    <MenuItem value="75">75 - Excessive PIN Tries</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={loadData}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && summary.totals && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Transactions
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(summary.totals.totalTransactions)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Dispensed
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.totals.totalDispensed)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Surcharge
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.totals.totalSurcharges)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Approval Rate
                  </Typography>
                  <Typography variant="h5">
                    {summary.totals.approvalRatePercent ? `${summary.totals.approvalRatePercent}%` : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Amount
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.totals.avgTransactionAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Transaction List" />
              <Tab label="Response Code Analysis" />
            </Tabs>
          </Box>

          <CardContent>
            {activeTab === 0 && (
              <>
                {/* Transaction List */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>Sequence</TableCell>
                        <TableCell>Network Seq</TableCell>
                        <TableCell>RRN</TableCell>
                        {user?.role === 'admin' && <TableCell>Terminal ID</TableCell>}
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Requested</TableCell>
                        <TableCell align="right">Dispensed</TableCell>
                        <TableCell align="right">Sale</TableCell>
                        <TableCell align="right">Tip</TableCell>
                        <TableCell align="right">Cash Back</TableCell>
                        <TableCell align="right">Surcharge</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Response</TableCell>
                        <TableCell>PAN</TableCell>
                        {user?.role === 'admin' && <TableCell>Source</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: pageSize }, (_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell><CircularProgress size={16} /></TableCell>
                            <TableCell>...</TableCell>
                            <TableCell>...</TableCell>
                            {user?.role === 'admin' && <TableCell>...</TableCell>}
                            <TableCell>...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell align="right">...</TableCell>
                            <TableCell>...</TableCell>
                            <TableCell>...</TableCell>
                            {user?.role === 'admin' && <TableCell>...</TableCell>}
                          </TableRow>
                        ))
                      ) : transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={user?.role === 'admin' ? 11 : 9} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No transactions found for the selected date range
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactions.map((transaction, index) => (
                          <TableRow key={transaction.id || index}>
                            <TableCell>
                              {formatDateTime(transaction.date, transaction.time, transaction.timezone, transaction.localDateTime)}
                            </TableCell>
                            <TableCell>{transaction.sequenceNumber ?? '-'}</TableCell>
                            <TableCell>{transaction.networkSequenceNumber ?? '-'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary">
                                {transaction.rrn || '-'}
                              </Typography>
                            </TableCell>
                            {user?.role === 'admin' && (
                              <TableCell>
                                <Box>
                                  {transaction.traceId ? (
                                    <Tooltip title={`Trace: ${transaction.traceId}`}>
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                          {transaction.actualTerminalId || transaction.terminalId}
                                        </Typography>
                                        {transaction.actualTerminalId && transaction.translatedTerminalId && 
                                         transaction.actualTerminalId !== transaction.translatedTerminalId && (
                                          <>
                                            <SwapHoriz sx={{ fontSize: 12, color: 'text.secondary' }} />
                                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary">
                                              {transaction.translatedTerminalId}
                                            </Typography>
                                          </>
                                        )}
                                        <Tooltip title="Enhanced transaction data available">
                                          <Info sx={{ fontSize: 12, color: 'primary.main' }} />
                                        </Tooltip>
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                      {transaction.terminalId || id}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                            <TableCell>
                              <Chip 
                                label={transaction.transactionType}
                                color={transactionTypeColor(transaction.transactionType)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(transaction.requestedAmount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(transaction.dispensedAmount)}
                            </TableCell>
                            {/* Sale / Tip / Cash Back exist only on the terminal's reporting push; a row that
                                never came through reporting shows "–" (unknown), not $0.00 (reported as zero). */}
                            <TableCell align="right">
                              {transaction.hasReportingData && transaction.saleAmount != null ? formatCurrency(transaction.saleAmount) : '–'}
                            </TableCell>
                            <TableCell align="right">
                              {transaction.hasReportingData ? formatCurrency(transaction.tipAmount || 0) : '–'}
                            </TableCell>
                            <TableCell align="right">
                              {transaction.hasReportingData ? formatCurrency(transaction.cashBackAmount || 0) : '–'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(transaction.surchargeAmount)}
                            </TableCell>
                            {/* Total = Dispensed + Surcharge. Nothing dispensed (decline / reversal) → $0.00, not the requested surcharge. */}
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency((transaction.dispensedAmount || 0) > 0 ? (transaction.dispensedAmount || 0) + (transaction.surchargeAmount || 0) : 0)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.isReversed ? 'Reversed' : `${transaction.responseCode} - ${transaction.responseDescription || 'N/A'}`}
                                color={transaction.isReversed ? 'warning' : getResponseCodeColor(transaction.responseCode)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {transaction.panLastFour ? `****${transaction.panLastFour}` : 'N/A'}
                            </TableCell>
                            {user?.role === 'admin' && (
                              <TableCell>
                                <Box>
                                  <Chip
                                    label={transaction.sourceType || 'manual'}
                                    size="small"
                                    color={
                                      transaction.sourceType === 'polled' ? 'primary' :
                                      transaction.sourceType === 'live' ? 'success' : 'default'
                                    }
                                    variant={transaction.apiVersion === 'v2' ? 'filled' : 'outlined'}
                                  />
                                  {transaction.apiVersion === 'v2' && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      API v2
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={2}>
                  {/* Page Size Selector */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="textSecondary">
                      Rows per page:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Page Info */}
                  {totalRecords > 0 && (
                    <Typography variant="body2" color="textSecondary">
                      Showing {getPageInfo().start}-{getPageInfo().end} of {formatNumber(totalRecords)} transactions
                    </Typography>
                  )}

                  {/* Pagination Component */}
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(event, value) => setPage(value)}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size="medium"
                    />
                  )}
                </Box>

                {transactions.length === 0 && !loading && (
                  <Box textAlign="center" py={4}>
                    <Typography color="textSecondary">
                      No transactions found for the selected criteria
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              <>
                {analytics && analytics.responseCodeDistribution ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Response Code Analysis
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Breakdown of transaction response codes for the selected date range
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {analytics.responseCodeDistribution.map((responseCode, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card 
                            variant="outlined" 
                            sx={{ 
                              height: '100%',
                              border: responseCode.responseCategory === 'APPROVED' ? '2px solid #4caf50' :
                                     responseCode.responseCategory === 'DECLINED' ? '2px solid #f44336' :
                                     responseCode.responseCategory === 'ERROR' ? '2px solid #ff9800' :
                                     responseCode.responseCategory === 'PARTIAL' ? '2px solid #2196f3' :
                                     '1px solid #e0e0e0'
                            }}
                          >
                            <CardContent>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Chip 
                                  label={responseCode.responseCategory || 'UNKNOWN'}
                                  color={
                                    responseCode.responseCategory === 'APPROVED' ? 'success' :
                                    responseCode.responseCategory === 'DECLINED' ? 'error' :
                                    responseCode.responseCategory === 'ERROR' ? 'warning' :
                                    responseCode.responseCategory === 'PARTIAL' ? 'info' :
                                    'default'
                                  }
                                  size="small"
                                />
                                <Typography variant="h6" fontWeight="bold">
                                  {responseCode.responseCode}
                                </Typography>
                              </Box>
                              
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {responseCode.responseDescription || 'Unknown Description'}
                              </Typography>
                              
                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                <Typography variant="h5" fontWeight="bold">
                                  {formatNumber(responseCode.count)}
                                </Typography>
                                <Typography variant="h6" color="primary">
                                  {responseCode.percentage}%
                                </Typography>
                              </Box>
                              
                              <Box 
                                sx={{ 
                                  width: '100%', 
                                  height: '8px', 
                                  backgroundColor: '#e0e0e0', 
                                  borderRadius: '4px',
                                  mt: 1,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box 
                                  sx={{ 
                                    width: `${Math.min(responseCode.percentage, 100)}%`, 
                                    height: '100%', 
                                    backgroundColor: responseCode.responseCategory === 'APPROVED' ? '#4caf50' :
                                                   responseCode.responseCategory === 'DECLINED' ? '#f44336' :
                                                   responseCode.responseCategory === 'ERROR' ? '#ff9800' :
                                                   responseCode.responseCategory === 'PARTIAL' ? '#2196f3' :
                                                   '#9e9e9e',
                                    borderRadius: 'inherit'
                                  }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    
                    {/* Summary Stats */}
                    <Box mt={4}>
                      <Typography variant="h6" gutterBottom>
                        Summary Statistics
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Total Response Codes
                              </Typography>
                              <Typography variant="h6">
                                {analytics.responseCodeDistribution.length}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Most Common
                              </Typography>
                              <Typography variant="h6">
                                {analytics.responseCodeDistribution[0] != null && analytics.responseCodeDistribution[0].responseCode != null
                                  ? analytics.responseCodeDistribution[0].responseCode
                                  : 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {analytics.responseCodeDistribution[0]?.percentage || 0}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Approved Rate
                              </Typography>
                              <Typography variant="h6" color="success.main">
                                {analytics.responseCodeDistribution
                                  .filter(rc => rc.isSuccessful === true)
                                  .reduce((sum, rc) => sum + rc.percentage, 0)
                                  .toFixed(1)}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Error Rate
                              </Typography>
                              <Typography variant="h6" color="error.main">
                                {analytics.responseCodeDistribution
                                  .filter(rc => rc.responseCategory === 'ERROR' || rc.responseCategory === 'DECLINED')
                                  .reduce((sum, rc) => sum + rc.percentage, 0)
                                  .toFixed(1)}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color="textSecondary">
                      {loading ? 'Loading response code analysis...' : 'No response code data available for the selected date range'}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TerminalTransactions;